import React, { useState } from "react";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import Button from "../../ui/Button";
import Dropdown from "../../ui/SelectDropdown";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchPublicContractByIdAsync,
  fetchPublicContractCountsAsync,
  fetchPublicContractsAsync,
  updatePublicContractStatusAsync,
} from "../../../redux/features/publicContractSlice";
import { PublicContractStatus } from "../../../redux/features/types/publicContractTypes";
import {
  getContractCode,
  labelPublicContractStatus,
  publicContractStatusUpdateOptions,
} from "./util";

interface UpdateStatusModalProps {
  open: boolean;
  onClose: () => void;
  contract: any;
  /** Optional list query to refetch after a successful update. */
  listQuery?: Record<string, any>;
}

const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  open,
  onClose,
  contract,
  listQuery,
}) => {
  const dispatch = useAppDispatch();
  const { updating } = useAppSelector((state) => state.publicContracts);
  const [status, setStatus] = useState<string>(contract?.status ?? "");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  const currentStatus = contract?.status as string | undefined;
  const code = getContractCode(contract);

  const handleClose = () => {
    setConfirming(false);
    setError("");
    setStatus(contract?.status ?? "");
    onClose();
  };

  const handleContinue = () => {
    if (!status) {
      setError("Select a status");
      return;
    }
    if (status === currentStatus) {
      setError("Choose a different status from the current one");
      return;
    }
    setError("");
    setConfirming(true);
  };

  const handleConfirm = async () => {
    if (!contract?.id) return;
    try {
      await dispatch(
        updatePublicContractStatusAsync({
          id: contract.id,
          status: status as PublicContractStatus,
        })
      ).unwrap();
      dispatch(fetchPublicContractCountsAsync());
      if (listQuery) {
        dispatch(fetchPublicContractsAsync(listQuery));
      }
      dispatch(fetchPublicContractByIdAsync(contract.id));
      handleClose();
    } catch {
      setConfirming(false);
    }
  };

  return (
    <CustomModalContainer
      open={open}
      onClose={handleClose}
      title={confirming ? "Confirm status change" : "Update contract status"}
      footer={
        confirming ? (
          <div className="flex justify-between gap-3">
            <Button
              type="button"
              label="Back"
              layout="inline"
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={updating}
            />
            <Button
              type="button"
              label="Confirm update"
              layout="inline"
              onClick={handleConfirm}
              isLoading={updating}
            />
          </div>
        ) : (
          <div className="flex justify-between gap-3">
            <Button
              type="button"
              label="Cancel"
              layout="inline"
              variant="ghost"
              onClick={handleClose}
            />
            <Button
              type="button"
              label="Continue"
              layout="inline"
              onClick={handleContinue}
            />
          </div>
        )
      }
    >
      {confirming ? (
        <p className="text-gray-700 text-sm leading-6">
          Update{" "}
          <span className="font-semibold">{code}</span> from{" "}
          <span className="font-semibold">
            {labelPublicContractStatus(currentStatus)}
          </span>{" "}
          to{" "}
          <span className="font-semibold">
            {labelPublicContractStatus(status)}
          </span>
          ? This writes directly to the live contract.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            Contract <span className="font-semibold text-gray-800">{code}</span>
            {currentStatus ? (
              <>
                {" "}
                is currently{" "}
                <span className="font-semibold">
                  {labelPublicContractStatus(currentStatus)}
                </span>
                .
              </>
            ) : null}
          </p>
          <Dropdown
            id="invoice-update-status"
            name="invoiceStatus"
            label="New status"
            options={publicContractStatusUpdateOptions}
            value={status}
            onSelect={(value) => {
              setStatus(value);
              setError("");
            }}
            placeholder="Select status"
            required
            error={error}
          />
        </div>
      )}
    </CustomModalContainer>
  );
};

export default UpdateStatusModal;
